import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { IListing } from 'src/services/api/listings/types';
import type { IProperty } from 'src/services/api/properties/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { queryKeys } from 'src/services/api/constants';

type UpdateListingPropertyResponse = {
    listing: IListing;
    num_leads_updated: number;
};

type UpdateListingPropertyPayload = {
    listingId: number;
    propertyId: number;
    leadUuids: string[];
    overwriteImage?: boolean;
};

export const useUpdateListingProperty = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        UpdateListingPropertyResponse,
        IStandardErrorResponse,
        UpdateListingPropertyPayload
    >(async ({ listingId, propertyId, leadUuids, overwriteImage }) => {
        const res = await apiClient(`api/landlord/listings/${listingId}/property`, {
            method: 'patch',
            json: {
                property_id: propertyId,
                lead_uuids: leadUuids,
                overwrite_image: overwriteImage ?? false,
            },
        }).json<UpdateListingPropertyResponse>();

        return res;
    }, {
        onSuccess: (data, { propertyId, overwriteImage }) => {
            queryClient.setQueryData<IListing>([queryKeys.listings, data.listing.id], data.listing);
            queryClient.invalidateQueries(queryKeys.listings);
            if (overwriteImage) {
                // overwrite copies the listing's image onto the property, and both URLs are
                // built by the same serializer from the same s3 path, so the property's new
                // image_url is exactly the listing's image_url. Patch the caches directly.
                const newImageUrl = data.listing.image_url;
                queryClient.setQueryData<IProperty[]>([queryKeys.properties], (oldData) =>
                    (oldData ?? []).map((property) =>
                        property.id === propertyId ? { ...property, image_url: newImageUrl ?? null } : property,
                    ),
                );
            }
            addNotification('Property updated successfully', 'success');
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Failed to update property';
            addNotification(errorMessage, 'error');
        },
    });
};
